// İSG uzmanı portalı — üç belge ekleme modalının (firma §13.2, personel §15.1, araç §23.2) ORTAK tür
// ve geçerlilik kuralı mantığı (GL #7; plan D8/D9, Review Focus 2/4/5). Tür listesi §27 kaydından
// gelir; türe özgü istemci kodu yoktur, davranış yalnız seçilen türün `expiryRule`'udur:
//   - `EndDateRequired` → bitiş tarihi görünür ve zorunludur (yıldız + zod), kural ipucu gösterilir;
//   - `EndDateOptional` → görünür, isteğe bağlıdır, kural ipucu gösterilir;
//   - `NoExpiry` ya da tür seçilmemiş → alan gizlidir; değer tür değişince boşaltılır ve ASLA
//     gönderilmez (sunucu okumaz).
// Varsayılan tür YOKTUR: "Belge türü seçin" + zorunlu doğrulama.
// Sunucu bir tür alanını reddederse (`INVALID`/`INACTIVE`, §27.6) kayıt tazelenir, seçim temizlenir,
// alan hatası gösterilir ve odak seçiciye gider — aynı gövde asla yeniden gönderilmez.
// Saf kural yardımcıları (`validUntilToSend`, `missingRequiredValidUntil`, `withDocumentType`)
// web-supplier ile ortaktır ve `@isgportal/shared`'dadır; burada yalnız RHF bağlaması kalır.

import {
  type DocumentEntity,
  type DocumentTypeDto,
  isTypeFieldRejection,
  missingRequiredValidUntil,
  requiresValidUntil,
  showsValidUntil,
  useDocumentTypes,
  validUntilToSend,
  withDocumentType,
} from '@isgportal/shared';
import type { TFunction } from 'i18next';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Ekleme formunun şeması. `validUntil` zorunluluğu seçilen türün kuralından okunur (`superRefine`);
 * şema kayıt değiştikçe yeniden kurulur (çağıran `types`'ı `useMemo` bağımlılığına koyar).
 */
export function documentLinkSchema(
  t: TFunction,
  types: readonly DocumentTypeDto[] | undefined,
  entity: DocumentEntity,
  note: z.ZodString = z.string().trim(),
) {
  const typeRequired = t('documentTypes.required');
  return z
    .object({
      type: z.string({ required_error: typeRequired }).min(1, typeRequired),
      validFrom: z.string().optional(),
      validUntil: z.string().optional(),
      note: note.optional(),
    })
    .superRefine((values, ctx) => {
      if (missingRequiredValidUntil(types, entity, values)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['validUntil'],
          message: t('documents.field.validUntilRequired'),
        });
      }
    });
}

export type DocumentLinkFormValues = z.infer<ReturnType<typeof documentLinkSchema>>;

/**
 * Seçilen türü ve onun `validUntil` kuralını forma bağlar. `typeField` seçicinin kaydıdır (tür
 * değişince gizlenen tarih boşaltılır, tarih hatası yeniden doğrulanır); `handleTypeRejection`
 * mutasyonun hata yolunda çağrılır ve hata bir tür reddiyse `true` döner.
 */
export function useDocumentLinkType(
  entity: DocumentEntity,
  form: UseFormReturn<DocumentLinkFormValues>,
) {
  const { t } = useTranslation();
  const registry = useDocumentTypes();
  const { register, watch, getValues, setValue, clearErrors, trigger, setError, setFocus } = form;
  // Render sırasında okunur (RHF formState vekili yalnız okunan alanlara abone olur).
  const submitted = form.formState.isSubmitted;

  const selected = registry.find(entity, watch('type'));
  const showValidUntil = showsValidUntil(selected);

  const typeField = register('type', {
    onChange: () => {
      const values = getValues();
      const next = withDocumentType(registry.types, entity, values, values.type);
      if (next.validUntil !== values.validUntil) {
        setValue('validUntil', next.validUntil ?? '');
      }
      // Kural değişti: tarih hatası yeni kurala göre yeniden hesaplanır (gönderimden önce hiç yok).
      if (submitted) {
        void trigger('validUntil');
      } else {
        clearErrors('validUntil');
      }
    },
  });

  function handleTypeRejection(error: unknown): boolean {
    if (!isTypeFieldRejection(error)) {
      return false;
    }
    void registry.refetch();
    setValue('type', '');
    setValue('validUntil', '');
    setError('type', { type: 'server', message: t('documentTypes.typeRejected') });
    setFocus('type');
    return true;
  }

  return {
    typeField,
    showValidUntil,
    requireValidUntil: requiresValidUntil(selected),
    expiryHint:
      selected && showValidUntil ? t(`documents.expiryHint.${selected.expiryRule}`) : undefined,
    /** Kaydın güncel görüntüsüyle `validUntilToSend` (alan gizliyse `null`). */
    validUntilToSend: (values: DocumentLinkFormValues) =>
      validUntilToSend(registry.types, entity, values),
    handleTypeRejection,
  };
}

/** `useDocumentLinkType`'ın dönüşü — `ValidUntilField` kuralı buradan okur. */
export type DocumentLinkType = ReturnType<typeof useDocumentLinkType>;
